import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { nameof } from "../../../../../../utils/nameof";
import { AssetIncomeFormValues } from "../../IncomeView.types";
import { createEmptyIncome } from "../../IncomeView.utils";
import { IncomeSectionCard } from "../income-section-card/IncomeSectionCard";
import { IncomeEntryCard } from "./components/income-entry-card/IncomeEntryCard";
import { SpousalMaintenanceDialog } from "./components/spousal-maintenance-dialog/SpousalMaintenanceDialog";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.list";

interface Props {
  formik: FormikProps<AssetIncomeFormValues>;
}

export function IncomeListSection({ formik }: Props) {
  const { t } = useTranslation();
  const [isSpousalMaintenanceOpen, setIsSpousalMaintenanceOpen] = useState(false);

  const fieldName = nameof<AssetIncomeFormValues>("incomes");
  const incomes = formik.values.incomes;

  const onAdd = () => {
    void formik.setFieldValue(fieldName, [...incomes, createEmptyIncome(crypto.randomUUID())]);
  };

  const onRemove = (index: number) => {
    void formik.setFieldValue(
      fieldName,
      incomes.filter((_, i) => i !== index)
    );
  };

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <Alert severity="info">
        <Stack gap={1}>
          <Typography variant="body2">{t(`${SECTION}.notice`)}</Typography>
          <Typography variant="body2">
            <Trans
              i18nKey={`${SECTION}.spousalMaintenanceNotice`}
              components={{
                calcLink: (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setIsSpousalMaintenanceOpen(true)}
                    sx={{ p: 0, minWidth: 0, fontWeight: 700, textTransform: "none", verticalAlign: "baseline" }}
                  />
                ),
              }}
            />
          </Typography>
        </Stack>
      </Alert>

      {incomes.map((entry, index) => (
        <IncomeEntryCard key={entry.key} formik={formik} index={index} entry={entry} onRemove={onRemove} />
      ))}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd} sx={{ alignSelf: "flex-end" }}>
        {t(`${SECTION}.add`)}
      </Button>

      {isSpousalMaintenanceOpen && <SpousalMaintenanceDialog onClose={() => setIsSpousalMaintenanceOpen(false)} />}
    </IncomeSectionCard>
  );
}
